import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, CheckCheck, X, MessageSquare, Factory, UserCog, Truck, Calculator, Box, Package, Shield, Gift, Info } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { erpApi } from '../utils/erpApi';
import { useAuth } from '../contexts/AuthContext';

// Roles that use the customer/dealer/driver portal instead of the ERP bell.
// Mirrors NON_INTERNAL_ROLES in backend/routers/erp_notifications.py so the
// component never fires requests the backend would 403.
const NON_INTERNAL_ROLES = new Set(['customer', 'dealer', 'driver', '']);

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const TYPE_ICONS = {
  'new_inquiry': MessageSquare,
  'new_contact': MessageSquare,
  'order': Package,
  'production': Factory,
  'hr': UserCog,
  'transport': Truck,
  'finance': Calculator,
  'inventory': Box,
  'security': Shield,
  'reward': Gift,
};

const PRIORITY_COLORS = {
  high: 'bg-red-500',
  medium: 'bg-amber-500',
  important: 'bg-amber-500',
  normal: 'bg-teal-500',
  critical: 'bg-red-500',
};

const getWsUrl = () => {
  const wsProtocol = API_BASE?.startsWith('https') ? 'wss' : 'ws';
  const wsHost = API_BASE?.replace(/^https?:\/\//, '');
  return `${wsProtocol}://${wsHost}/api/realtime/ws/erp-notifications`;
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

const ERPNotificationBell = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = (user?.role || '').toLowerCase();
  const isInternal = !NON_INTERNAL_ROLES.has(role);
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const pingRef = useRef(null);
  const containerRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const [listRes, countRes] = await Promise.all([
        erpApi.erpNotifications.get(),
        erpApi.erpNotifications.getUnreadCount(),
      ]);
      const list = listRes?.data?.notifications || [];
      setNotifications(list);
      setUnread(countRes?.data?.unread_count || 0);
    } catch (e) {
      // Ignore auth/network errors quietly
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch on mount + poll fallback (internal roles only; portal roles have
  // their own notification UI and the ERP bell endpoints reject them)
  useEffect(() => {
    if (!isInternal) {
      setLoading(false);
      return;
    }
    refresh();
    const poll = setInterval(refresh, 60000);
    return () => clearInterval(poll);
  }, [refresh, isInternal]);

  // WebSocket real-time
  useEffect(() => {
    if (!isInternal) return;
    let ws = null;
    const connect = () => {
      try {
        ws = new WebSocket(getWsUrl());
        ws.onopen = () => {
          setConnected(true);
          if (reconnectRef.current) { clearTimeout(reconnectRef.current); reconnectRef.current = null; }
          pingRef.current = setInterval(() => {
            if (ws?.readyState === WebSocket.OPEN) ws.send('ping');
          }, 25000);
        };
        ws.onmessage = (event) => {
          try {
            const msg = JSON.parse(event.data);
            if (msg.type === 'erp_notification') {
              refresh();
              if (open) setOpen((prev) => prev);
            }
          } catch (e) { /* ignore */ }
        };
        ws.onclose = () => {
          setConnected(false);
          if (pingRef.current) { clearInterval(pingRef.current); pingRef.current = null; }
          reconnectRef.current = setTimeout(connect, 5000);
        };
        ws.onerror = () => {
          try { ws?.close(); } catch (e) { /* ignore */ }
        };
      } catch (e) { /* ignore */ }
    };
    connect();
    return () => {
      if (pingRef.current) clearInterval(pingRef.current);
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      try { ws?.close(); } catch (e) { /* ignore */ }
    };
  }, [refresh, open]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const markRead = async (id) => {
    try { await erpApi.erpNotifications.markRead(id); } catch (e) { /* ignore */ }
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    setUnread((prev) => (prev > 0 ? prev - 1 : 0));
  };

  const markAllRead = async () => {
    try { await erpApi.erpNotifications.markAllRead(); } catch (e) { /* ignore */ }
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnread(0);
  };

  const handleItemClick = (n) => {
    if (!n.read) markRead(n.id);
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  if (!isInternal) return null;

  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={() => setOpen((prev) => !prev)}
        className="relative flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors"
        aria-label="Notifications"
        title="Notifications"
      >
        <Bell className="h-4.5 w-4.5 h-[18px] w-[18px]" />
        {unread > 0 && (
          <span className="absolute -top-1.5 -right-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-12 z-50 w-80 sm:w-96 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl">
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
            <div className="flex items-center gap-2">
              <Bell className="h-4 w-4 text-teal-600" />
              <span className="text-sm font-semibold text-slate-800">Notifications</span>
              {unread > 0 && (
                <span className="rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-600">{unread} new</span>
              )}
              <span className="ml-1 flex items-center gap-1">
                <span className={`h-1.5 w-1.5 rounded-full ${connected ? 'bg-green-500' : 'bg-slate-300'}`} />
                <span className="text-[10px] text-slate-400">{connected ? 'live' : 'offline'}</span>
              </span>
            </div>
            <button
              onClick={markAllRead}
              className="flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium text-teal-600 hover:bg-teal-50"
            >
              <CheckCheck className="h-3.5 w-3.5" /> Mark all read
            </button>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-10 text-sm text-slate-400">Loading…</div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-slate-400">
                <Bell className="h-8 w-8 mb-2 opacity-40" />
                <p className="text-sm">You're all caught up</p>
              </div>
            ) : (
              notifications.map((n) => {
                const Icon = TYPE_ICONS[n.type] || Info;
                const dotColor = PRIORITY_COLORS[n.priority] || 'bg-teal-500';
                return (
                  <button
                    key={n.id}
                    onClick={() => handleItemClick(n)}
                    className={`flex w-full items-start gap-3 border-b border-slate-50 px-4 py-3 text-left transition-colors hover:bg-slate-50 ${n.read ? 'bg-white' : 'bg-teal-50/50'}`}
                  >
                    <div className={`mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full ${n.read ? 'bg-slate-100 text-slate-400' : 'bg-teal-100 text-teal-600'}`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-[13px] font-medium leading-snug ${n.read ? 'text-slate-600' : 'text-slate-800'}`}>
                          {n.title}
                        </p>
                        {!n.read && <span className={`mt-1 h-2 w-2 flex-shrink-0 rounded-full ${dotColor}`} />}
                      </div>
                      <p className="mt-0.5 line-clamp-2 text-[12px] leading-snug text-slate-500">{n.message}</p>
                      <p className="mt-1 text-[11px] text-slate-400">{timeAgo(n.created_at)}</p>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ERPNotificationBell;
